/**
 * Tusq · "Add another color" widget for the Zoorix cart drawer
 * =============================================================
 * Adds a color-swatch row inside Zoorix's drawer so shoppers can add another
 * COLOR of any cart line item without leaving the drawer. Hero product (Body
 * Shaper Tall Tank) is the primary target but the widget shows for ANY cart
 * item whose product has a "Color" option with 2+ values.
 *
 * SAFETY — same bulletproof pattern as tusq-zoorix-drawer-inject-strip.js:
 *   - No polling. Purely event-driven.
 *   - Observer scoped to the Zoorix cart container only.
 *   - Idempotent: rebuilds only when the cart-state key changes.
 *   - Uses Shopify's own /products/{handle}.js + /cart/add.js — no custom endpoints.
 *   - The only cart mutation this file performs is the shopper's explicit
 *     click on a color pill (with a size preselected). Never touches cart
 *     otherwise, so it cannot cause a sold-out/inventory-reserve loop.
 */
(function () {
  'use strict';

  if (window.__tusqColorPickerInit) return;
  window.__tusqColorPickerInit = true;

  var ITEMS_SELECTOR = '.zrx-cart-items';
  var WRAP_SELECTOR = '.zrx-cart-wrapper';
  var CONTAINER_SELECTOR = '.zrx-cart-container, .zrx-cart-wrapper';
  var STRIP_ID = 'tusq-color-picker-injected';
  var COMPLETE_STRIP_ID = 'tusq-clt-injected'; // "Complete the look" — we insert BEFORE this if present

  var productCache = {};
  var lastBlocks = null;   // Map<product_id, blockHtml> — cached between refreshes
  var lastKey = '';
  var fetching = false;

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function drawerOpen() {
    var w = document.querySelector(WRAP_SELECTOR);
    return !!w && !/(^|\s)closed(\s|$)/.test(w.className || '');
  }

  function fetchProduct(handle) {
    if (productCache[handle]) return Promise.resolve(productCache[handle]);
    return fetch('/products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) { if (p) productCache[handle] = p; return p; })
      .catch(function () { return null; });
  }

  function optionByName(product, regex) {
    if (!product || !product.options) return null;
    for (var i = 0; i < product.options.length; i++) {
      var o = product.options[i];
      var name = (typeof o === 'string' ? o : (o && o.name)) || '';
      if (regex.test(name.toLowerCase())) {
        return { name: name, index: i, values: (typeof o === 'string' ? (product['options_with_values'] || [{}])[i].values : o.values) || [] };
      }
    }
    return null;
  }

  function variantOptionAt(v, index) {
    return v['option' + (index + 1)];
  }

  function findVariant(product, colorIdx, color, sizeIdx, size) {
    return (product.variants || []).find(function (v) {
      var okColor = variantOptionAt(v, colorIdx) === color;
      var okSize = sizeIdx == null || variantOptionAt(v, sizeIdx) === size;
      return okColor && okSize;
    });
  }

  function buildItemBlock(cartItem, product) {
    var colorOpt = optionByName(product, /color|colour/);
    if (!colorOpt || !colorOpt.values || colorOpt.values.length < 2) return '';
    var sizeOpt = optionByName(product, /size/);

    // Detect the color + size of THIS cart line item, by looking up its variant on the product
    var currentVariant = (product.variants || []).find(function (v) { return v.id === cartItem.variant_id; });
    var currentColor = currentVariant ? variantOptionAt(currentVariant, colorOpt.index) : null;
    var currentSize = (currentVariant && sizeOpt) ? variantOptionAt(currentVariant, sizeOpt.index) : null;

    // Other colors available (exclude current)
    var otherColors = colorOpt.values.filter(function (c) { return c !== currentColor; });
    if (!otherColors.length) return '';

    // Compute whether each color has ANY available variant (across ALL sizes).
    // A pill is only marked sold-out if EVERY size of that color is out of stock,
    // OR if we've narrowed to a specific size and that size is out for that color.
    // Since the shopper can freely change the size dropdown, we start with the
    // "any available" definition and update on size-change (see updatePillAvailability).
    var pills = otherColors.map(function (color) {
      var anyAvail = (product.variants || []).some(function (v) {
        return variantOptionAt(v, colorOpt.index) === color && v.available;
      });
      var initialMatch = findVariant(product, colorOpt.index, color, sizeOpt ? sizeOpt.index : null, currentSize);
      var initialAvail = !!(initialMatch && initialMatch.available);
      // Start pill as available if the color has ANY available size — the shopper can pick a different size in the dropdown.
      var available = anyAvail;
      var vid = initialMatch ? initialMatch.id : '';
      return (
        '<button type="button" class="tusq-color-picker__pill' + (available ? '' : ' tusq-color-picker__pill--sold-out') + '" ' +
          'data-tusq-color-pill data-color="' + esc(color) + '" data-variant-id="' + esc(vid) + '"' +
          ' data-size-avail="' + (initialAvail ? '1' : '0') + '"' +
          (available ? '' : ' disabled') + ' ' +
          'aria-label="Add ' + esc(color) + ' — ' + esc(product.title) + (available ? '' : ' (sold out in every size)') + '">' +
          esc(color) +
        '</button>'
      );
    }).join('');

    var sizeControl = '';
    if (sizeOpt && sizeOpt.values.length > 1) {
      var opts = sizeOpt.values.map(function (s) {
        var sel = (s === currentSize) ? ' selected' : '';
        return '<option value="' + esc(s) + '"' + sel + '>' + esc(s) + '</option>';
      }).join('');
      sizeControl =
        '<label class="tusq-color-picker__size">' +
          '<span class="tusq-color-picker__size-label">Size</span>' +
          '<select data-tusq-color-size aria-label="Choose size">' + opts + '</select>' +
        '</label>';
    }

    return (
      '<div class="tusq-color-picker__item" data-tusq-color-block ' +
        'data-product-handle="' + esc(product.handle) + '" ' +
        'data-product-id="' + esc(product.id) + '" ' +
        'data-color-index="' + colorOpt.index + '"' +
        (sizeOpt ? ' data-size-index="' + sizeOpt.index + '"' : '') + '>' +
        '<div class="tusq-color-picker__head">' +
          '<span class="tusq-color-picker__title">Add another color</span>' +
          '<span class="tusq-color-picker__sub">' + esc(product.title) + '</span>' +
        '</div>' +
        '<div class="tusq-color-picker__row">' +
          '<div class="tusq-color-picker__pills">' + pills + '</div>' +
          sizeControl +
        '</div>' +
        '<div class="tusq-color-picker__msg" data-tusq-color-msg aria-live="polite"></div>' +
      '</div>'
    );
  }

  // Returns a Map of product_id -> block HTML (one block per unique product)
  function build(cartItems) {
    var handles = [];
    var seen = {};
    cartItems.forEach(function (it) {
      if (it.handle && !seen[it.handle]) { seen[it.handle] = true; handles.push(it.handle); }
    });
    return Promise.all(handles.map(fetchProduct)).then(function () {
      var byProductId = {};
      cartItems.forEach(function (it) {
        var p = productCache[it.handle];
        if (!p) return;
        var pid = String(p.id);
        // Only build ONE block per product_id (first cart line for that product wins).
        // If the same product is in cart multiple times (unusual), one widget below the first line covers it.
        if (byProductId[pid]) return;
        var html = buildItemBlock(it, p);
        if (html) byProductId[pid] = html;
      });
      return byProductId;
    });
  }

  // Track injected block nodes by product_id so we can re-place / remove them cleanly.
  var placedBlocks = {}; // { pid: HTMLElement }

  function place(blocksByPid) {
    if (!drawerOpen()) { return; }
    var items = document.querySelector(ITEMS_SELECTOR);
    if (!items) return;

    // Snapshot current line items → their product-ids
    var lineItems = [].filter.call(items.children, function (el) {
      return el.classList && el.classList.contains('zrx-cart-item');
    });
    var seenPidAsLine = {};

    // Insert / move each block into position right after its matching (first) line item
    lineItems.forEach(function (li) {
      var pid = li.getAttribute('data-product-id');
      if (!pid || seenPidAsLine[pid]) return;
      seenPidAsLine[pid] = true;
      var html = blocksByPid && blocksByPid[pid];
      if (!html) return;
      var existing = placedBlocks[pid];
      if (!existing || !existing.isConnected) {
        var tpl = document.createElement('template');
        tpl.innerHTML = html;
        existing = tpl.content.firstElementChild;
        placedBlocks[pid] = existing;
      }
      // Ensure it sits immediately after `li` (idempotent)
      if (li.nextElementSibling !== existing) {
        li.parentNode.insertBefore(existing, li.nextElementSibling);
      }
    });

    // Remove any placed blocks whose product is no longer in cart
    Object.keys(placedBlocks).forEach(function (pid) {
      if (!seenPidAsLine[pid] || !(blocksByPid && blocksByPid[pid])) {
        var el = placedBlocks[pid];
        if (el && el.parentNode) el.parentNode.removeChild(el);
        delete placedBlocks[pid];
      }
    });
  }

  function refreshFromCart() {
    if (fetching) return;
    fetching = true;
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var items = cart.items || [];
        // Cache-buster key: variants + qtys, so identical repeated calls do nothing
        var key = items.map(function (i) { return i.variant_id + 'x' + i.quantity; }).join(',');
        if (key === lastKey && lastBlocks) {
          place(lastBlocks);
          return;
        }
        lastKey = key;
        // Cart changed — drop any stale placed nodes so they can be rebuilt from new HTML
        Object.keys(placedBlocks).forEach(function (pid) {
          var el = placedBlocks[pid];
          if (el && el.parentNode) el.parentNode.removeChild(el);
          delete placedBlocks[pid];
        });
        return build(items).then(function (blocksByPid) {
          lastBlocks = blocksByPid;
          place(blocksByPid);
        });
      })
      .catch(function () {})
      .then(function () { fetching = false; });
  }

  var pending = null;
  function scheduleRefresh() {
    if (pending) return;
    pending = setTimeout(function () { pending = null; refreshFromCart(); }, 200);
  }

  // Re-evaluate each pill in an item block for the currently-selected size.
  // Called on size dropdown change so the pill's visual state + variant id
  // reflect the currently-picked size. A color that has NO available size
  // is still marked sold-out (rare).
  function updatePillAvailability(itemBlock) {
    var handle = itemBlock.getAttribute('data-product-handle');
    var product = productCache[handle];
    if (!product) return;
    var colorIdx = parseInt(itemBlock.getAttribute('data-color-index'), 10);
    var sizeIdxAttr = itemBlock.getAttribute('data-size-index');
    var sizeIdx = sizeIdxAttr == null ? null : parseInt(sizeIdxAttr, 10);
    var sizeSelect = itemBlock.querySelector('[data-tusq-color-size]');
    var size = sizeSelect ? sizeSelect.value : null;
    var pills = itemBlock.querySelectorAll('[data-tusq-color-pill]');
    pills.forEach(function (pill) {
      var color = pill.getAttribute('data-color');
      var match = findVariant(product, colorIdx, color, sizeIdx, size);
      var availForSize = !!(match && match.available);
      pill.setAttribute('data-variant-id', match ? match.id : '');
      pill.setAttribute('data-size-avail', availForSize ? '1' : '0');
      // Only fully disable a pill if that color has NO available size at all.
      var anyAvail = (product.variants || []).some(function (v) {
        return variantOptionAt(v, colorIdx) === color && v.available;
      });
      pill.disabled = !anyAvail;
      pill.classList.toggle('tusq-color-picker__pill--sold-out', !anyAvail);
    });
  }

  // Size dropdown change → refresh pill availability
  document.addEventListener('change', function (e) {
    var sel = e.target && e.target.matches && e.target.matches('[data-tusq-color-size]') ? e.target : null;
    if (!sel) return;
    var itemBlock = sel.closest('.tusq-color-picker__item');
    if (itemBlock) updatePillAvailability(itemBlock);
  });

  // ---- Click handler: color pill → resolve variant → add to cart ---- //
  document.addEventListener('click', function (e) {
    var pill = e.target.closest && e.target.closest('[data-tusq-color-pill]');
    if (!pill || pill.disabled) return;
    e.preventDefault();
    var itemBlock = pill.closest('.tusq-color-picker__item');
    if (!itemBlock) return;
    var handle = itemBlock.getAttribute('data-product-handle');
    var product = productCache[handle];
    if (!product) return;
    var colorIdx = parseInt(itemBlock.getAttribute('data-color-index'), 10);
    var sizeIdxAttr = itemBlock.getAttribute('data-size-index');
    var sizeIdx = sizeIdxAttr == null ? null : parseInt(sizeIdxAttr, 10);
    var color = pill.getAttribute('data-color');
    var sizeSelect = itemBlock.querySelector('[data-tusq-color-size]');
    var size = sizeSelect ? sizeSelect.value : null;

    var v = findVariant(product, colorIdx, color, sizeIdx, size);
    var msg = itemBlock.querySelector('[data-tusq-color-msg]');
    if (!v || !v.available) {
      // Try to auto-suggest an available size for this color
      var alt = (product.variants || []).find(function (vv) {
        return variantOptionAt(vv, colorIdx) === color && vv.available;
      });
      if (alt && sizeSelect && sizeIdx != null) {
        var altSize = variantOptionAt(alt, sizeIdx);
        // Update the size dropdown to an available size, refresh pill state, and prompt shopper
        sizeSelect.value = altSize;
        updatePillAvailability(itemBlock);
        if (msg) msg.textContent = size + ' is sold out in ' + color + '. Try ' + altSize + ' or pick another size, then tap ' + color + ' again.';
      } else {
        if (msg) msg.textContent = color + ' is sold out in every size.';
      }
      return;
    }

    pill.setAttribute('data-adding', '1');
    pill.disabled = true;
    if (msg) msg.textContent = 'Adding ' + color + '…';

    fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: v.id, quantity: 1 })
    })
      .then(function (r) { return r.ok ? r.json() : Promise.reject(new Error('add failed')); })
      .then(function () {
        if (msg) msg.textContent = 'Added ' + color + ' — ' + (size || '') + ' to cart.';
        pill.removeAttribute('data-adding');
        // Force refresh from cart (this also updates the strip so the newly-added color is excluded)
        setTimeout(refreshFromCart, 400);
        // Fire cart update events the theme listens for
        document.dispatchEvent(new CustomEvent('cart:updated'));
      })
      .catch(function () {
        if (msg) msg.textContent = 'Could not add ' + color + '. Try again.';
        pill.disabled = false;
        pill.removeAttribute('data-adding');
      });
  });

  // ---- Network hook: catch any cart mutation → refresh ---- //
  function isCartMutation(u) { return typeof u === 'string' && /\/cart\/(add|change|update|clear)/.test(u); }
  function installNetworkHook() {
    if (window.fetch && !window.fetch.__tusqCpHooked) {
      var of = window.fetch;
      window.fetch = function (input) {
        var u = typeof input === 'string' ? input : (input && input.url);
        var pr = of.apply(this, arguments);
        if (isCartMutation(u)) pr.then(function () { setTimeout(refreshFromCart, 300); }).catch(function () {});
        return pr;
      };
      window.fetch.__tusqCpHooked = true;
    }
    var XP = window.XMLHttpRequest && window.XMLHttpRequest.prototype;
    if (XP && !XP.__tusqCpHooked) {
      var oopen = XP.open, osend = XP.send;
      XP.open = function (m, url) { this.__tusqCpUrl = url; return oopen.apply(this, arguments); };
      XP.send = function () {
        if (isCartMutation(this.__tusqCpUrl)) {
          this.addEventListener('load', function () { setTimeout(refreshFromCart, 300); });
        }
        return osend.apply(this, arguments);
      };
      XP.__tusqCpHooked = true;
    }
  }

  function attachObserver() {
    var host = document.querySelector(CONTAINER_SELECTOR);
    if (!host) return false;
    new MutationObserver(function () { if (drawerOpen()) scheduleRefresh(); }).observe(host, { childList: true, subtree: true });
    return true;
  }

  function start() {
    installNetworkHook();
    refreshFromCart();

    document.addEventListener('cart:refresh', refreshFromCart);
    document.addEventListener('cart:updated', refreshFromCart);
    document.addEventListener('click', function (e) {
      if (e.target.closest && e.target.closest('.zrx-cart-toggle, [class*="zrx-cart-tog"]')) {
        setTimeout(refreshFromCart, 300);
      }
    }, true);

    if (!attachObserver()) {
      var boot = new MutationObserver(function () {
        if (attachObserver()) { boot.disconnect(); scheduleRefresh(); }
      });
      boot.observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
